import React, { useEffect } from 'react';
import ReactDOM from 'react-dom/client';
import { useTranslation } from 'react-i18next';
import { BrowserRouter, useLocation, useNavigationType } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Grommet } from 'grommet';
import { observer } from 'mobx-react-lite';
import devFavicon from 'url:/public/images/dev-favicon.png';
import '../src/styles/productTour.css';
import '../src/styles/toast-styles.css';
import { config } from './config';
import './config/locales-translations';
import { LanguageSelector, RootErrorBoundary, SessionManager } from '/src/components';
import { RootContextProvider, useRootStore, useUserStore } from '/src/context';
import '/src/index.css';
import '/src/lib/sentry/instrument';
import { AppRoutes } from '/src/routes';
import theme from '/src/theme';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 60000,
      gcTime: 600000,
      refetchOnWindowFocus: false,
    },
  },
});

const ScrollToTop = () => {
  const location = useLocation();
  const navigationType = useNavigationType();
  const { listRestoreStore } = useRootStore();

  useEffect(() => {
    if (navigationType === 'POP' && listRestoreStore.consumeRestore(location.pathname)) return;
    setTimeout(() => {
      window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
    }, 0);
  }, [location.pathname, navigationType]);

  return null;
};

const FuseAdminEffects = observer(() => {
  const { isFuseAdmin } = useUserStore();
  const { i18n } = useTranslation();

  useEffect(() => {
    const className = 'is-fuse-admin';

    if (isFuseAdmin) {
      document.body.classList.add(className);
      if (i18n.language !== 'en') i18n.changeLanguage('en');
    } else {
      document.body.classList.remove(className);
    }

    return () => {
      document.body.classList.remove(className);
    };
  }, [isFuseAdmin, i18n]);

  return null;
});

const rootElement = document.getElementById('app');

if (process.env.NODE_ENV === 'development') {
  const devFaviconUrl = new URL(devFavicon, config.client.url).href;
  document.getElementById('favicon')?.setAttribute('href', devFaviconUrl);
}

rootElement &&
  ReactDOM.createRoot(rootElement).render(
    <React.StrictMode>
      <QueryClientProvider client={queryClient}>
        <RootContextProvider>
          <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
            <ScrollToTop />
            <FuseAdminEffects />
            <SessionManager />
            <Grommet theme={theme}>
              <RootErrorBoundary>
                <AppRoutes />
                <LanguageSelector />
                <ToastContainer
                  position="top-right"
                  autoClose={1750}
                  hideProgressBar={false}
                  newestOnTop={false}
                  pauseOnFocusLoss={false}
                  draggable={false}
                  closeOnClick
                  pauseOnHover
                  closeButton={false}
                  style={{ top: '60px' }}
                />
              </RootErrorBoundary>
            </Grommet>
          </BrowserRouter>
        </RootContextProvider>
      </QueryClientProvider>
    </React.StrictMode>
  );
